/* eslint-disable no-param-reassign */
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';

/* External */
import { connect, useSelector } from 'react-redux';
import { compose } from 'redux';
import { useTranslation } from 'react-i18next';
import { dispatchesActions } from 'screens/Dispatches/reducer';

/* External */
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import StyledCardHeader from 'commons/components/StyledCardHeader';
import Autocomplete from '@material-ui/lab/Autocomplete';
import DeleteIcon from '@material-ui/icons/Delete';
import { Print } from '@material-ui/icons';
import produce from 'immer';

/* Project */
import { actionProps } from 'utils/reduxActions';
import {
  clone,
  falsy,
  fCurrency,
  fNumber,
  getUnitDetails,
  onChangeFnc,
  truthty,
} from 'utils/functions';
import { splitToLotId } from 'utils/dataReader';
import saga from '../../saga';
import connectSaga from '../../../../utils/saga';
import ButtonWithLoader from '../../../../commons/containers/ButtonWithLoader';
import StyledTableCell from '../../../../commons/components/StyledTableCell';
import DatePicker from '../../../../commons/components/DatePicker';
import QRScanner from '../../../../commons/components/QRScanner';
import styles from './styles';

const useStyles = makeStyles(styles);

const parseLotIdFromCode = (raw) => {
  if (!raw) return '';
  const code = String(raw).trim();

  if (/^\d+$/.test(code)) return code;

  if (code.startsWith('PK,')) {
    const pakPart = code
      .split(',')
      .find((part) => part.startsWith('p:'))
      ?.split(':')[1];
    return pakPart && /^\d+$/.test(pakPart) ? pakPart : '';
  }

  const legacyMatch = code.match(/id\s+(\d+)/i);
  return legacyMatch ? legacyMatch[1] : '';
};

function DispatchForm(props) {
  const { t } = useTranslation();
  const { objects, actions, toggleForm } = props;

  const toEditId = useSelector((state) => state.dispatches.toEditId);
  const lastDispatch = useSelector((state) => state.dispatches.lastDispatch);
  const isOpen = useSelector((state) => state.dispatches.formIsOpen);
  const edit = useSelector((state) => state.dispatches.edit);
  const stateControls = useSelector((state) => state.dispatches.formControls);
  const stateInvalidControls = useSelector((state) => state.dispatches.invalidControls);

  const [state, setState] = useState({
    loading: false,
    errorMsg: '',
    inputValue: '',
    focused: false,
    inputVal: '',
    count: 0,
    showCamaraButton: false,
    camaraIsOpen: false,
  });
  const [qrBuffer, setQrBuffer] = useState('');
  const savedCallback = useRef();
  const codeInput = useRef();

  const { unitCode, unitName } = getUnitDetails(objects.accounts);

  const newLastDispatch = Number(lastDispatch) + 1;

  const [controls, setControls] = useState({ ...stateControls });
  const [invalidControls, setInvalidControls] = useState({ ...stateInvalidControls });

  const [isPrinting, setIsPrinting] = useState(false);

  const printHandler = async () => {
    await new Promise(() => {
      setIsPrinting(true);
    });
  };

  useEffect(() => {
    const mediaQueryList = window.matchMedia('print');

    const handleMediaChange = (e) => {
      setIsPrinting(e.matches);
    };

    mediaQueryList.addListener(handleMediaChange);

    return () => {
      mediaQueryList.removeListener(handleMediaChange);
    };
  }, []);

  useEffect(() => {
    const handleBeforePrint = () => {
      setIsPrinting(false);
    };

    if (isPrinting) {
      console.log('Printing...');
      print();
      window.addEventListener('beforeprint', handleBeforePrint);
    }

    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
    };
  }, [isPrinting]);

  const settingControls = (changes) => {
    setControls({ ...controls, ...changes });
  };

  const settingInvalidControls = (changes) => {
    setInvalidControls({ ...invalidControls, ...changes });
  };

  const settingProductValues = (changes) => {
    setControls({ ...controls, productValues: { ...controls.productValues, ...changes } });
    setInvalidControls({ ...invalidControls, productValues: false });
  };

  const changeControls = (event, date) => {
    onChangeFnc(settingControls, event, date);
  };

  const changeProductValues = (event, date) => {
    const { value } = event.target;
    if (value === '' || parseFloat(value) >= 0) {
      onChangeFnc(settingProductValues, event, date);
    }
  };

  const submit = (dispatchNumber) => {
    let productValues = false;
    controls.lots.forEach((l) => {
      if (falsy(controls.productValues[l.productId])) {
        productValues = true;
      }
    });
    if (
      falsy(dispatchNumber) ||
      falsy(controls.clientId) ||
      falsy(controls.lots) ||
      productValues
    ) {
      settingInvalidControls({
        productValues,
        dispatchNumber: falsy(dispatchNumber),
        clientId: falsy(controls.clientId),
        lots: falsy(controls.lots),
      });
    } else {
      actions.changeControls(controls, invalidControls);
      // eslint-disable-next-line no-unused-expressions
      edit
        ? actions.editDispatch(toEditId, controls)
        : actions.createDispatch({ ...controls, dispatchNumber });
    }
  };

  useEffect(() => {
    // eslint-disable-next-line no-use-before-define
    savedCallback.current = callback;
  });

  useEffect(() => {
    if (!toEditId) {
      actions.newDispatch();
    }

    function tick() {
      savedCallback.current();
    }
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (truthty(invalidControls.lots)) {
      setState(
        produce((draft) => {
          draft.errorMsg = t('At least one pak required');
        }),
      );
    }
  }, [invalidControls.lots]);

  const onKeyPress = () => {
    if (!state.loading) {
      setState(
        produce((draft) => {
          draft.loading = true;
        }),
      );
    }
  };

  const validateCode = (code) => {
    console.log('==> Validating dispatch code: ', code);
    let msg = '';
    const id = parseLotIdFromCode(code);
    const usedLot = controls.lots.find((lot) => lot.id === Number(id));

    const lot = Object.values(objects.lots).find(
      (lotIt) => falsy(lotIt.destinyProcessId) && lotIt.id === Number(id),
    );

    if (truthty(id) && truthty(lot) && falsy(usedLot)) {
      msg = '';
    } else if (truthty(usedLot)) {
      msg = t('Used pak error');
    } else if (falsy(lot)) {
      msg = t('Dispatched pak error');
    } else {
      msg = t('Invalid code error');
    }

    setState(
      produce((draft) => {
        draft.loading = false;
        draft.inputValue = '';
        draft.count = 0;
      }),
    );
    return msg;
  };

  const lotButtonOnClick = () => {
    if (codeInput.current) {
      codeInput.current.focus();
      setState(
        produce((draft) => {
          draft.errorMsg = '';
          draft.showCamaraButton = true;
        }),
      );
    }
  };

  const callback = () => {
    if (falsy(codeInput.current)) {
      // eslint-disable-next-line no-undef
      codeInput.current = document.getElementById('lotInput');
    }

    if (state.count <= 0 && truthty(state.inputValue)) {
      setState(
        produce((draft) => {
          draft.count += 1;
        }),
      );
    }

    if (state.count > 0 && state.inputVal !== state.inputValue) {
      setState(
        produce((draft) => {
          draft.inputVal = state.inputValue;
          draft.count = 0;
        }),
      );
    } else if (state.count > 0 && state.inputVal === state.inputValue) {
      const errorMsg = validateCode(state.inputValue);

      if (errorMsg === '') {
        const val = parseLotIdFromCode(state.inputValue);
        settingControls(
          {
            lots: [...controls.lots, objects.lots[Number(val)]],
          },
          { lots: false },
        );
        setState(
          produce((draft) => {
            draft.loading = false;
            draft.inputValue = '';
            draft.count = 0;
          }),
        );
        if (truthty(codeInput.current)) {
          codeInput.current.blur();
        }
      }
      setState(
        produce((draft) => {
          draft.errorMsg = errorMsg;
        }),
      );
    }
  };

  const onFocus = () => {
    setState(
      produce((draft) => {
        draft.focused = true;
      }),
    );
  };

  const onBlur = () => {
    setState(
      produce((draft) => {
        draft.focused = false;
      }),
    );
  };

  const lotOnChange = (event) => {
    event.persist();
    const { value } = event.target;
    console.log({ valueOnChange: value, qrBuffer });
    if (value.length === 1 && (value.includes('¿') || qrBuffer.length > 0)) {
      // Acumulamos los caracteres primero para poder obtener el id especifico
      const newBuffer = qrBuffer + value;
      console.log({ newBuffer });
      setQrBuffer(newBuffer);

      // Cuando encuentre el # termine de acumular
      if (value === '#') {
        const match = newBuffer.match(/¿id\s+(\d+)/);
        if (match) {
          setState(
            produce((draft) => {
              // eslint-disable-next-line prefer-destructuring
              draft.inputValue = match[1];
            }),
          );
        }
        setQrBuffer('');
      }
    } else {
      setQrBuffer('');

      if (value.startsWith('PK,')) {
        const parsedId = parseLotIdFromCode(value);
        setState(
          produce((draft) => {
            draft.inputValue = parsedId;
          }),
        );
        return;
      }

      setState(
        produce((draft) => {
          draft.inputValue = value;
        }),
      );
    }
  };

  const handleQrReader = async (value) => {
    const lotId = Number(parseLotIdFromCode(value));
    setState(
      produce((draft) => {
        draft.inputValue = lotId;
      }),
    );
  };

  const deleteLot = (event) => {
    const itemIndex = Number(event.currentTarget.dataset.index);
    settingControls(
      {
        lots: controls.lots.filter((lot, index) => index !== itemIndex),
      },
      { lots: false },
    );
  };

  const handleCamaraButton = () => {
    setState(
      produce((draft) => {
        draft.camaraIsOpen = !state.camaraIsOpen;
      }),
    );
  };

  const classes = useStyles();
  const quantityByProduct = {};

  const productIds = [...new Set(controls.lots.map((lot) => lot.productId))];
  (function fillQuantityByProduct() {
    productIds.forEach((productId) => {
      const lotsByProduct = controls.lots.filter((lot) => lot.productId === productId);

      quantityByProduct[productId] = lotsByProduct.reduce((acc, lot) => acc + lot.quantity, 0);
    });
  })();

  const clientsOptions = useMemo(
    () => [
      '',
      ...Object.values(objects.clients)
        .map((c) => ({ id: c.id, name: c.entity().name }))
        .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)),
    ],
    [objects.clients],
  );

  const datePicker = (
    <DatePicker
      inputId="date"
      label={t('Dispatch date')}
      fullWidth
      value={controls.date}
      onChange={changeControls}
    />
  );

  const dispatchNumberInput = (
    <TextField
      value={controls.dispatchNumber || newLastDispatch}
      onChange={changeControls}
      fullWidth
      id="dispatchNumber"
      label={t('Dispatch number')}
      error={invalidControls.dispatchNumber}
      helperText={invalidControls.dispatchNumber && t('Enter a dispatch number')}
    />
  );

  const observationInput = (
    <TextField
      value={controls.observation}
      onChange={changeControls}
      fullWidth
      multiline
      id="observation"
      label={t('Address')}
      error={invalidControls.observation}
    />
  );

  const clientSelect = (
    <>
      <Autocomplete
        value={
          controls.clientId && clientsOptions.find((client) => client.id === controls.clientId)
        }
        id="autocomplete-clients-dispatches"
        options={clientsOptions}
        getOptionLabel={(client) => client && client.name}
        renderInput={(params) => (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <TextField style={{ margin: 0 }} {...params} label={t('Client')} margin="normal" />
        )}
        onChange={(event, newValue) => {
          const e = {
            autocomplete: true,
            input: 'clientId',
            value: newValue ? newValue.id : '',
          };
          changeControls(e);
        }}
      />
      {truthty(invalidControls.clientId) && (
        <FormHelperText error>{t('Client required error')}</FormHelperText>
      )}
    </>
  );

  let labelLotButton;
  if (state.loading) {
    labelLotButton = t('Loading label');
  } else if (state.focused) {
    labelLotButton = t('Scanning pak label');
  } else {
    labelLotButton = t('Scan or enter pak label');
  }

  const addLotButton = (
    <Box display="flex" alignItems="flex-end" justifyContent="center" style={{ height: '100%' }}>
      <Button onClick={lotButtonOnClick} color="primary" variant="contained">
        {labelLotButton}
      </Button>
    </Box>
  );

  const hiddenScannerTextField = (
    <TextField
      value={state.inputValue}
      onChange={lotOnChange}
      id="lotInput"
      label={t('Enter pak number')}
      onKeyDown={onKeyPress}
      autoComplete="off"
      onFocus={onFocus}
      onBlur={onBlur}
    />
  );

  const lotsDetailTable = (
    <>
      <Grid item xs={12} md={6}>
        {state.camaraIsOpen && (
          <div className="no-print">
            <QRScanner onScan={handleQrReader} />
          </div>
        )}
      </Grid>
      <div className="table-print" style={{ marginTop: '20px', width: '97%' }}>
        <Grid item xs={12}>
          <TableContainer component={Paper} className={isPrinting ? '' : classes.tableRoot}>
            <Table size="small" className="table-print">
              <TableHead>
                <TableRow>
                  <StyledTableCell>N°</StyledTableCell>
                  <StyledTableCell align="right">{t('Species')}</StyledTableCell>
                  <StyledTableCell align="right">{t('Variety')}</StyledTableCell>
                  <StyledTableCell align="right">{t('Product')}</StyledTableCell>
                  <StyledTableCell align="right">{t('No. paks header')}</StyledTableCell>
                  <StyledTableCell align="right">{t('Quantity')}</StyledTableCell>
                  <StyledTableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {controls.lots.map((row, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <TableRow key={`${row.description}-${index}`}>
                    <StyledTableCell component="th" scope="row">
                      {row.id}
                    </StyledTableCell>
                    <StyledTableCell component="th" scope="row">
                      {row.fruit().name}
                    </StyledTableCell>
                    <StyledTableCell align="right">{row.variety().name}</StyledTableCell>
                    <StyledTableCell align="right">{row.product().name}</StyledTableCell>
                    <StyledTableCell align="right">{`${row.packagingQuantity} [${
                      row.packagingType().name
                    }]`}</StyledTableCell>
                    <StyledTableCell align="right">{`${row.quantity.toFixed(
                      2,
                    )} [${unitCode}]`}</StyledTableCell>
                    <StyledTableCell align="right">
                      <div className="no-print">
                        <IconButton data-index={index} onClick={deleteLot}>
                          <DeleteIcon />
                        </IconButton>
                      </div>
                    </StyledTableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </div>
    </>
  );

  const createValueInput = (productId) => (
    <TextField
      value={controls.productValues[productId] || ''}
      onChange={changeProductValues}
      fullWidth
      id={String(productId)}
      type="number"
      label={t('Value per unit label', { unit: unitName })}
      error={invalidControls.productValues && falsy(controls.productValues[productId])}
      helperText={
        truthty(invalidControls.productValues) &&
        falsy(controls.productValues[productId]) &&
        t('Value required error')
      }
    />
  );

  const createValueSectionByProduct = (productId) => {
    const product = objects.products[productId];
    const totalQuantity = quantityByProduct[productId];
    const totalValue = totalQuantity * controls.productValues[productId];

    return (
      <Fragment key={`product-value-${productId}`}>
        <TableRow>
          <StyledTableCell align="left">{product.name}</StyledTableCell>
          <StyledTableCell align="center">{`${totalQuantity.toFixed(
            2,
          )} ${unitCode}`}</StyledTableCell>
          <StyledTableCell align="center">{createValueInput(productId)}</StyledTableCell>
          <StyledTableCell align="center">{`${fCurrency.format(
            isNaN(totalValue) ? 0 : totalValue,
          )}`}</StyledTableCell>
        </TableRow>
      </Fragment>
    );
  };

  const valueByLotInputList = controls.lots
    .map((lot) => lot.productId)
    .filter((item, index, self) => self.indexOf(item) === index)
    .map((productId) => createValueSectionByProduct(productId));

  // ------------------------------------------------------- Sections ---------------------------------------------------------
  const clientFormSection = (
    <>
      <Grid item xs={11}>
        <Typography gutterBottom variant="h6" component="h6">
          {t('Dispatch data section')}
        </Typography>
      </Grid>
      {controls.lots.length ? (
        <Grid item xs={1} className="no-print">
          <Print style={{ color: '#00897b', cursor: 'pointer' }} onClick={printHandler} />
        </Grid>
      ) : null}
      {!controls.date ? (
        <Grid item sm={6} xs={12} className="no-print">
          {datePicker}
        </Grid>
      ) : (
        <Grid item sm={6} xs={12}>
          {datePicker}
        </Grid>
      )}
      {!controls.dispatchNumber ? (
        <Grid item sm={6} xs={12} className="no-print">
          {dispatchNumberInput}
        </Grid>
      ) : (
        <Grid item sm={6} xs={12}>
          {dispatchNumberInput}
        </Grid>
      )}
      {!controls.clientId ? (
        <Grid item sm={6} xs={12} className="no-print">
          {clientSelect}
        </Grid>
      ) : (
        <Grid item sm={6} xs={12}>
          {clientSelect}
        </Grid>
      )}
      {!controls.observation ? (
        <Grid item sm={6} xs={12} className="no-print">
          {observationInput}
        </Grid>
      ) : (
        <Grid item sm={6} xs={12}>
          {observationInput}
        </Grid>
      )}
    </>
  );

  const addedLotsSection = (
    <>
      <Grid item xs={12}>
        <Typography gutterBottom variant="h6" component="h6">
          {t('Paks')}
        </Typography>
      </Grid>
      {truthty(state.errorMsg) && (
        <Box
          display="flex"
          justifyContent="center"
          style={{ width: '100%', marginTop: 10, marginBottom: 15 }}>
          <FormHelperText error>{state.errorMsg}</FormHelperText>
        </Box>
      )}
      <div className="no-print" style={{ width: '100%' }}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            {addLotButton}
          </Grid>
          <Grid item xs={4}>
            {hiddenScannerTextField}
          </Grid>
          <Grid item xs={4} hidden={!state.showCamaraButton}>
            <Button variant="contained" color="primary" onClick={handleCamaraButton}>
              {state.camaraIsOpen ? t('CLOSE_CAMERA') : t('OPEN_CAMERA')}
            </Button>
          </Grid>
        </Grid>
      </div>

      {lotsDetailTable}
    </>
  );

  const totalValueByProductSection = Boolean(controls.lots.length) && (
    <div className="table-print" style={{ marginTop: '20px', width: '97%' }}>
      <Grid item xs={12}>
        <Card className={classes.subModule}>
          <StyledCardHeader
            title={t('Total value by products title')}
            subheader={
              <>
                <Typography>
                  {t('Total quantity with unit label', {
                    unit: unitCode,
                    quantity: fNumber.format(
                      Object.values(controls.lots).reduce((acum, lot) => acum + lot.quantity, 0),
                    ),
                  })}
                </Typography>
                <Typography>
                  {t('Total value with symbol label', {
                    total: fNumber.format(
                      Object.values(controls.lots).reduce(
                        (acum, lot) => acum + lot.price * lot.quantity,
                        0,
                      ),
                    ),
                  })}
                </Typography>
              </>
            }
          />
        </Card>
        <TableContainer component={Paper} className={isPrinting ? '' : classes.tableRoot}>
          <Table size="small" className="table-print">
            <TableHead>
              <TableRow>
                <StyledTableCell align="left">{t('Product')}</StyledTableCell>
                <StyledTableCell align="center">
                  {t('Quantity')} {unitName}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {t('Price per kg')} {unitName}
                </StyledTableCell>
                <StyledTableCell align="center">{t('Value')}</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>{valueByLotInputList}</TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </div>
  );

  return (
    <Dialog fullWidth onClose={toggleForm} maxWidth="md" open={isOpen}>
      <div className="no-print">
        <DialogTitle>{`${edit ? t('Edit') : t('Create')} ${t(
          'Dispatch',
        ).toLowerCase()}`}</DialogTitle>
      </div>
      <DialogContent>
        <Grid container spacing={2} alignItems="center">
          {clientFormSection}
          {addedLotsSection}
        </Grid>
        <div>
          <Grid container item>
            {totalValueByProductSection}
          </Grid>
        </div>
      </DialogContent>
      <div className="no-print">
        <DialogActions>
          <Button onClick={toggleForm} color="primary">
            {t('Cancel')}
          </Button>
          <ButtonWithLoader
            onClick={() => submit(edit ? controls.dispatchNumber : newLastDispatch)}
            autoFocus
            buttonId="create-edit-dispatch">
            {edit ? t('Edit') : t('Add')}
          </ButtonWithLoader>
        </DialogActions>
      </div>
    </Dialog>
  );
}

const withSaga = connectSaga({ key: 'sagaDispatches', saga });

const withConnect = connect(
  undefined,
  actionProps(
    clone(clone.OBJECT, {
      newDispatch: dispatchesActions.newDispatch,
      editDispatch: dispatchesActions.editDispatch,
      toggleForm: dispatchesActions.toggleForm,
      createDispatch: dispatchesActions.createDispatch,
      changeControls: dispatchesActions.changeControls,
    }),
  ),
);

export default compose(withConnect)(React.memo(withSaga(DispatchForm)));
